/*
    FullscreenConfetti
    Convenience wrapper around Confetti, which passes in a width/height
    equal to the window size (or screen size, for mobile devices), and updates
    when that width/height changes.
*/

import * as React from "react";

import {useOnMountEffect} from "@khanacademy/wonder-blocks-core";
import {RequestInfo} from "@khan/shared-core";

import Confetti from "./confetti.tsx";
import {defaultProps, type Props} from "./props.ts";

const getWindowDimensions = () => {
    let windowWidth = window.innerWidth;
    let windowHeight = window.innerHeight;

    // HACK (josh): For some reason, the canvas gets stretched really
    // strangely in mobile safari when using window.innerWidth/innerHeight.
    // Instead, for mobile devices, just use the device screen size.
    if (RequestInfo.isPhone) {
        windowWidth = window.screen.width;
        windowHeight = window.screen.height;
    }

    return {windowWidth, windowHeight};
};

const defaultFullscreenProps = {
    ...defaultProps,
    gravity: 1100,
    spin: 4,
    twist: 20,
    emitDuration: 3000,
    spreadHorizontally: true,
    minSpeed: 200,
    maxSpeed: 600,
    minScale: 1,
    maxScale: 1,
} as const;

const FullscreenConfetti = ({onComplete, ...props}: Props) => {
    const [dimensions, setDimensions] = React.useState(getWindowDimensions());
    const [showConfetti, setShowConfetti] = React.useState(true);
    const confettiRef = React.useRef<React.ElementRef<typeof Confetti>>(null);

    useOnMountEffect(() => {
        confettiRef.current?.startConfetti(window.innerWidth / 2, -40);
        const handleResize = () => setDimensions(getWindowDimensions());
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    });

    const handleComplete = React.useCallback(() => {
        setShowConfetti(false);
        onComplete?.();
    }, [onComplete]);

    const visProp = showConfetti ? "visible" : "hidden";

    return (
        <div style={{visibility: visProp}}>
            <Confetti
                {...props}
                onComplete={handleComplete}
                ref={confettiRef}
                width={dimensions.windowWidth}
                height={dimensions.windowHeight}
            />
        </div>
    );
};

FullscreenConfetti.defaultProps = defaultFullscreenProps;

export default FullscreenConfetti;
